import cn from 'classnames';
import React from 'react';
import { CheckerBackdrop } from 'src/shared/components/CheckerBackdrop';
import { Shef, ShefWordmarkColor } from 'src/shared/design-system/Icon';
import { FeaturedShef } from './ShefTrio';
import { GateStepId, SignUpGateBreadcrumb } from './SignUpGateBreadcrumb';
import { SignUpGatePerks } from './SignUpGatePerks';

interface Props {
  backAction?: React.ReactNode;
  headline: React.ReactNode;
  subhead?: string;
  currentStep?: GateStepId;
  showPerks?: boolean;
  showChecker?: boolean;
  shefs?: FeaturedShef[];
  children: React.ReactNode;
}

export const SignUpGateLayout: React.FC<Props> = ({
  backAction,
  headline,
  subhead,
  currentStep = 'account',
  showPerks = false,
  showChecker = false,
  shefs,
  children,
}) => (
  <div className='relative flex min-h-dvh flex-col overflow-hidden bg-milk'>
    {showChecker && <CheckerBackdrop topClassName='top-[45svh]' />}

    <div className='relative z-10 flex flex-1 flex-col'>
      <div className='flex h-12 shrink-0 items-center px-4'>
        <Shef color={ShefWordmarkColor.RASPBERRY} />
      </div>

      <SignUpGateBreadcrumb currentStep={currentStep} className='shrink-0' />

      <div
        className={cn('relative z-10 flex flex-col items-center px-4 pt-6', {
          'gap-5': shefs,
          'gap-[15px]': !shefs,
        })}>
        <div className='flex w-full flex-col items-center gap-[17px]'>
          <div className='flex w-full flex-col items-center gap-3'>
            <div className='relative flex w-full justify-center'>
              {backAction && <div className='absolute left-0 top-1/2 -translate-y-1/2'>{backAction}</div>}
              <h1
                className={cn(
                  'max-w-[340px] text-center text-h3 uppercase text-eggplant',
                  'text-box-trim-cap',
                  'md:max-w-[358px]'
                )}>
                {headline}
              </h1>
            </div>
            {subhead && <p className='text-center text-body-400 text-eggplant-500'>{subhead}</p>}
          </div>

          {shefs && (
            <div className='flex justify-center gap-8'>
              {shefs.map((shef) => (
                <div className='flex w-20 flex-col items-center gap-[11px]' key={shef.name}>
                  <img className='size-20 max-w-none rounded-full object-cover' src={shef.image} alt={shef.name} />
                  {/* JA-WEIGHT-SHIM(1218377610766475): font-[600] renders Ja Bold under the shifted map; drop it for text-caption-700-caps when the map lands */}
                  <span className='flex items-center gap-0.5 whitespace-nowrap text-caption-700-caps font-[600] leading-[14px] text-eggplant'>
                    Shef <span className='font-quick-writers text-h5 leading-[14px] text-raspberry'>{shef.name}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={cn('w-full max-w-[400px]', { 'mt-[3px]': shefs })}>{children}</div>

        {/* Tablet 562 then desktop back to 500 is what the frames draw, not a typo. */}
        {showPerks && (
          <SignUpGatePerks className='mt-[38px] w-full max-w-[500px] pb-[30px] md:max-w-[562px] lg:max-w-[500px]' />
        )}
      </div>
    </div>
  </div>
);
